import { Injectable } from '@angular/core';
import { HttpHelpService } from 'src/app/Data/service/http-help.service';
import { Company, CompanyData, CompanyResponse, CompanyType } from './alldocuments-companies.model';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

/**
 * Prefer Hebrew, then English, then Arabic (API often leaves some fields empty).
 */
export function companyLabelHeEnAr(
  company: Partial<Pick<CompanyData, 'company_name_he' | 'company_name_en' | 'company_name_ar'>> | null | undefined,
): string {
  if (!company) return '';
  const he = String(company.company_name_he ?? '').trim();
  const en = String(company.company_name_en ?? '').trim();
  const ar = String(company.company_name_ar ?? '').trim();
  if (he) return he;
  if (en) return en;
  if (ar) return ar;
  return '';
}

@Injectable({
  providedIn: 'root'
})
export class CompaniesService {
  private companiesSubject = new BehaviorSubject<Company[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);

  public companies$ = this.companiesSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();

  constructor(private httpHelpService: HttpHelpService) { }

  private mapCompaniesResponse(response: any): Company[] {
    if (response && Array.isArray(response as CompanyResponse)) {
      return response.map((company: CompanyData) => {
        const label = companyLabelHeEnAr(company).trim() || 'Unnamed Company';
        return {
          name: label,
          code: company.company_id?.toString() ?? null,
          label,
          value: company.company_id?.toString() ?? null,
          fullData: company,
        };
      });
    }
    return [];
  }

  /** Active companies only — use for document creation forms. */
  getCompanies(): Observable<Company[]> {
    this.loadingSubject.next(true);

    return this.httpHelpService.GetCompany().pipe(
      map((response: any) => {
        const companiesArr = this.mapCompaniesResponse(response);
        this.companiesSubject.next(companiesArr);
        return companiesArr;
      }),
      catchError(error => {
        console.error('%c🔴 [COMPANIES-SERVICE] Error fetching companies:', 'color: red; font-weight: bold; font-size: 14px;', error);
        return of([]);
      }),
      tap(() => this.loadingSubject.next(false))
    );
  }

  /** All companies including archived — use for list/filter/view contexts. */
  getAllCompanies(): Observable<Company[]> {
    this.loadingSubject.next(true);

    return this.httpHelpService.GetAllCompaniesIncludingArchived().pipe(
      map((response: any) => this.mapCompaniesResponse(response)),
      catchError(error => {
        console.error('%c🔴 [COMPANIES-SERVICE] Error fetching all companies:', 'color: red; font-weight: bold; font-size: 14px;', error);
        return of([]);
      }),
      tap(() => this.loadingSubject.next(false))
    );
  }

  filterCompanies(companies: Company[], filterValue: string): Company[] {
    if (!filterValue) {
      return [...companies];
    }
    
    const lowerFilter = filterValue.toLowerCase();
    return companies.filter(company => 
      company.label && company.label.toLowerCase().includes(lowerFilter)
    );
  }

  isSingleCompany(companies: Company[]): boolean {
    const uniqueCompanyCodes = new Set(companies.map(c => c.code));
    return uniqueCompanyCodes.size <= 1;
  }

  getCompanyById(companyId: string | number): Company | undefined {
    if (!companyId || !this.companiesSubject.value.length) {
      return undefined;
    }

    const idString = companyId.toString();
    return this.companiesSubject.value.find(company => 
      company.code === idString || company.value === idString
    );
  }

  getCompanyFullDataById(companyId: string | number): CompanyData | undefined {
    const company = this.getCompanyById(companyId);
    return company?.fullData;
  }

  getCompanyInvoiceTypes(companyId: string | number): number[] {
    const companyData = this.getCompanyFullDataById(companyId);
    
    let invoiceTypes = companyData?.companyType?.invoice_types || [];
    
    // If no companyType data or empty invoice_types, provide default invoice types
    if (invoiceTypes.length === 0) {
      // Default invoice types that all companies should have access to
      invoiceTypes = [305, 400, 330, 320, 332, 100, 200, 0]; // Common invoice types
    }
    
    return invoiceTypes;
  }
}
